import { z } from 'zod';

export const TimeEntryItem = z.object({
  entryId: z.string().uuid(),
  localWorkDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  regularMinutes: z.number().int().nonnegative(),
  timeZone: z.string(),
});
export type TimeEntryItem = z.infer<typeof TimeEntryItem>;

// Why a line can no longer receive new hours. Covers BOTH kinds of row: an
// assignment-backed line (project → task) and an admin-category line (a standing
// bucket like "Gen Admin" or "Leave"). A named union rather than a free string
// (CLAUDE.md: no magic strings) so the badge copy, the client-side clamp message
// and the server's own precedence can never drift apart. `null` on the line
// means the row is open.
//
// Precedence is deliberate and matches how the server derives it.
//
// Assignment lines — project_archived → task_archived → unassigned: an archived
// project explains an archived project's tasks better than "not assigned" does,
// and `unassigned` is the residual, because activeFlag goes false for reasons
// that have nothing to do with archive (direct un-assignment, a team removed
// from a task).
//
// Category lines — category_inactive → category_locked → approver_only →
// not_granted. Four members rather than one `category_restricted`, because the
// conditions are independent: a category can be deactivated AND past its lock
// cutoff AND closed to this person simultaneously. A single member could not
// say which of them the server would actually refuse on, so the badge would
// explain a multiply-restricted row with the wrong reason — or, worse, a row the
// server still accepts would have to be reported as restricted to stay safe.
// `approver_only` and `not_granted` are mutually exclusive (a category has one
// visibility), so their relative order only fixes how the server's derivation
// reads, never which one wins on a real row.
//
// - `approver_only` — the category is visible to approvers only and this person
//   holds no approver authority.
// - `not_granted` — the category is assigned by leads and this person is not
//   granted it (no individual choice granting it, and no team of theirs set to
//   "whole team"; or an individual choice removing it). Deliberately NOT
//   `unassigned`, which already means "task assignment removed" and carries
//   task-specific copy.
//
// Both are ratchets, not walls: the line takes no NEW hours but a decrease
// still saves. The client must not re-derive any of these from the category
// catalog: `approver_only` needs two independently-stale caches (the catalog and
// `me.role`) to compute, and `not_granted` needs team settings and individual
// choices the client is never sent, so a freshly granted person would see a
// valid row rendered dimmed.
//
// DEPLOY SKEW: for one release the API never puts `not_granted` on the wire as
// `restrictedReason` — see `TimesheetLineItem.restrictedDetail`.
export const LineRestrictedReason = z.enum([
  'project_archived',
  'task_archived',
  'unassigned',
  'category_inactive',
  'category_locked',
  'approver_only',
  'not_granted',
]);
export type LineRestrictedReason = z.infer<typeof LineRestrictedReason>;

// A finer cause riding beside `restrictedReason`, for reasons that travel on the
// wire under an older member's name. Today only `not_granted`, sent with
// `restrictedReason: 'approver_only'`.
//
// Why the indirection: the SPA does not Zod-parse responses
// (`packages/web/src/api/client.ts` casts `response.json()`), so an SPA older
// than the API that received an unknown `restrictedReason` would index its copy
// maps with it and crash at render. `approver_only` has exactly the same
// server-side behavior (a ratchet), so an old SPA badges the row with slightly
// wrong copy and correct behavior; the new SPA reads `restrictedDetail ??
// restrictedReason` for copy. The follow-up release that drops the legacy
// `approver_only` column switches the wire reason to `not_granted` and removes
// this field.
export const LineRestrictedDetail = z.enum(['not_granted']);
export type LineRestrictedDetail = z.infer<typeof LineRestrictedDetail>;

export const TimesheetLineItem = z.object({
  lineId: z.string().uuid(),
  adminCategoryId: z.string().uuid().nullable(),
  assignmentId: z.string().uuid().nullable(),
  projectId: z.string().uuid().nullable(),
  description: z.string().nullable(),
  taskName: z.string().nullable(),
  projectName: z.string().nullable(),
  classificationName: z.string().nullable(),
  // Whether this line can still accrue NEW hours. Mirrors the server's own
  // ratchet exactly — assertAssignmentOpenForEntry (`activeFlag && project not
  // archived`) for assignment lines, the admin-category guard (active, not past
  // its lock cutoff, and not approver-only for this caller) for category lines —
  // so the grid never refuses a value the API would have accepted, or offers one
  // it would 409 on.
  //
  // Server-derived for BOTH kinds of row, and the client must not second-guess
  // it from the category catalog. Admin-category lines were previously exempt:
  // the field was hardcoded `true` and the client re-derived inactive/locked
  // itself. That exemption is gone, because approver-only cannot be derived
  // client-side at all — it needs two independently-stale caches (the catalog
  // and `me.role`), and a category line's openness now depends on WHO is
  // asking, which no catalog row records.
  //
  // The name is narrow for category lines — they have no assignment. Read it as
  // "this line is open for new hours". Renaming the field is a breaking wire
  // change and is deliberately deferred out of this feature rather than done
  // here.
  //
  // A restricted line is still RETURNED, with its project and task names intact
  // — "lines never disappear" (REQUIREMENTS.md). It renders dimmed and badged,
  // and it still accepts a value at or below what it already records so an
  // approver's "cut 40h to 20h" stays compliable.
  assignmentActive: z.boolean(),
  // Non-null exactly when `assignmentActive` is false — the two are derived
  // together server-side and must be read together.
  restrictedReason: LineRestrictedReason.nullable(),
  // See LineRestrictedDetail. Optional, not nullable-and-required, so a payload
  // from an API that predates the field (or a line with nothing finer to say)
  // parses unchanged. Only ever present alongside a non-null `restrictedReason`.
  restrictedDetail: LineRestrictedDetail.optional(),
  entries: z.array(TimeEntryItem),
});
export type TimesheetLineItem = z.infer<typeof TimesheetLineItem>;

export const TimesheetPeriod = z.object({
  weekStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  weekEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type TimesheetPeriod = z.infer<typeof TimesheetPeriod>;

export const ReturnCommentItem = z.object({
  comment: z.string(),
  actorDisplayName: z.string(),
});
export type ReturnCommentItem = z.infer<typeof ReturnCommentItem>;

export const TimesheetResponse = z.object({
  timesheetId: z.string().uuid(),
  status: z.string(),
  period: TimesheetPeriod,
  weeklyMinuteTarget: z.number().int(),
  isLocked: z.boolean(),
  returnComment: ReturnCommentItem.nullable(),
  // The sheet-level submit comment from the last submission (null if none), so
  // the submit dialog can pre-fill it when re-submitting a recalled sheet.
  comment: z.string().nullable(),
  lines: z.array(TimesheetLineItem),
});
export type TimesheetResponse = z.infer<typeof TimesheetResponse>;

// Read-only status of one week for a user. `missing` means no timesheet row
// exists yet for that week — reported without creating one (unlike GET
// /timesheets/current, which get-or-creates). Powers the client's last-week
// landing decision without materialising drafts or audit events.
export const WeekStatus = z.enum(['draft', 'submitted', 'approved', 'returned', 'missing']);
export type WeekStatus = z.infer<typeof WeekStatus>;

export const WeekStatusResponse = z.object({
  weekStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  status: WeekStatus,
  // Whether the user had an active assignment as of the probed week (scoped to
  // assignments that existed by the week's end). Lets the client nudge a
  // contributor to an un-started prior week (status `missing`) only when they
  // were actually expected to log time — never a brand-new hire whose first
  // assignment was created after that week.
  hasActiveAssignments: z.boolean(),
});
export type WeekStatusResponse = z.infer<typeof WeekStatusResponse>;

// The single definition of "this week still needs the contributor's action",
// consumed by both the post-login landing rule (`needsLastWeekAttention` in
// packages/web/src/pages/Timesheet.tsx) and the server-side outstanding-weeks
// scan that feeds the nudge. Two copies of this rule would produce the worst
// possible bug: a badge pointing at a week the landing rule ignores, or a
// redirect to a week the badge calls fine.
//
// A week needs action when:
//   - it is an unsubmitted draft or was returned for changes (a real sheet
//     exists that isn't finished), or
//   - it was never started (`missing`) but the contributor had assignments, so
//     they were expected to log time and simply haven't yet.
// Submitted/approved weeks are done, and a `missing` week for someone with
// nothing assigned (e.g. a brand-new hire whose prior week predates their
// start) is left alone. Anything unrecognised falls through to `false` so an
// unexpected status can never force a redirect or fabricate a nudge row.
export interface WeekAttentionInput {
  status: WeekStatus;
  hasActiveAssignments: boolean;
}

export function weekNeedsAttention({ status, hasActiveAssignments }: WeekAttentionInput): boolean {
  switch (status) {
    case 'draft':
    case 'returned':
      return true;
    case 'missing':
      return hasActiveAssignments;
    case 'submitted':
    case 'approved':
      return false;
    default:
      return false;
  }
}

// The statuses a week can carry once `weekNeedsAttention` has selected it: a
// finished week never reaches the outstanding list. Narrowed from `WeekStatus`
// rather than re-declared so the two enums cannot drift.
export const OutstandingWeekStatus = WeekStatus.extract(['draft', 'returned', 'missing']);
export type OutstandingWeekStatus = z.infer<typeof OutstandingWeekStatus>;

export const OutstandingWeek = z.object({
  weekStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  weekEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  status: OutstandingWeekStatus,
});
export type OutstandingWeek = z.infer<typeof OutstandingWeek>;

// Weeks earlier than last week that still need the signed-in contributor's
// action, most-recent-first and capped server-side, plus the uncapped
// `totalCount` so the badge's numeral and the popover's overflow line stay
// honest.
//
// Deliberately minimal: the resolved lookback floor and which term produced it
// (`FloorSource`) are diagnostics with no client consumer, so they stay in the
// repository's return type and the server log rather than in the response body.
export const OutstandingWeeksResponse = z.object({
  weeks: z.array(OutstandingWeek),
  totalCount: z.number().int().nonnegative(),
});
export type OutstandingWeeksResponse = z.infer<typeof OutstandingWeeksResponse>;

// Which term won the lookback-floor computation, for the server log only. Named
// rather than inline literals per CLAUDE.md's no-magic-strings rule. This never
// crosses the wire — do not add it to `OutstandingWeeksResponse`.
export type FloorSource = 'rolling_window' | 'system_start_date';

/**
 * Maximum length of a free-text note: a line note (`description`) and the
 * sheet-level submit comment. The API schemas and the SPA's textareas both read
 * this, so the UI counter can never promise more (or less) than the server accepts.
 */
export const NOTE_MAX_LENGTH = 1000;

// The system is week-granular: a line carries a single weekly total, not per-day
// entries. `totalMinutes` mirrors PutEntryRequest (0..10080 = one week of minutes).
export const PostLineRequest = z.object({
  adminCategoryId: z.string().uuid(),
  description: z.string().max(NOTE_MAX_LENGTH).optional(),
  totalMinutes: z.number().int().nonnegative().max(10080),
});
export type PostLineRequest = z.infer<typeof PostLineRequest>;

export const PostLineResponse = TimesheetLineItem;
export type PostLineResponse = z.infer<typeof PostLineResponse>;

export const PatchLineDescriptionRequest = z.object({
  description: z.string().max(NOTE_MAX_LENGTH).nullable(),
});
export type PatchLineDescriptionRequest = z.infer<typeof PatchLineDescriptionRequest>;

export const PutEntryRequest = z.object({
  totalMinutes: z.number().int().nonnegative().max(10080),
});
export type PutEntryRequest = z.infer<typeof PutEntryRequest>;

export const PutEntryResponse = TimeEntryItem;
export type PutEntryResponse = z.infer<typeof PutEntryResponse>;

export const TimesheetStatusResponse = z.object({
  timesheetId: z.string().uuid(),
  status: z.string(),
});
export type TimesheetStatusResponse = z.infer<typeof TimesheetStatusResponse>;

// Optional sheet-level comment a contributor may attach when submitting. The
// whole body is optional (a plain submit sends none).
export const SubmitTimesheetRequest = z.object({
  comment: z.string().max(NOTE_MAX_LENGTH).nullable().optional(),
});
export type SubmitTimesheetRequest = z.infer<typeof SubmitTimesheetRequest>;

export const UserHistoryEntry = z.object({
  timesheetId: z.string().uuid(),
  weekStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  weekEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  status: z.enum(['draft', 'submitted', 'approved', 'returned']),
  totalMinutes: z.number().int(),
  weeklyMinuteTarget: z.number().int(),
  submittedAt: z.string().datetime().nullable(),
  approvedAt: z.string().datetime().nullable(),
});
export type UserHistoryEntry = z.infer<typeof UserHistoryEntry>;

export const UserHistoryResponse = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  // Avatar initials, computed by the API from the AD name parts
  // (api/src/repositories/person.ts). The web renders this verbatim and
  // must never derive initials itself.
  initials: z.string(),
  entries: z.array(UserHistoryEntry),
  total: z.number().int(),
});
export type UserHistoryResponse = z.infer<typeof UserHistoryResponse>;
